import { useEffect } from "react";
import { HeroPortrait } from "@/components/masterclass/HeroPortrait";
import { HeroTimingOverlay } from "@/components/qa/HeroTimingOverlay";
import { SrcsetVerifier } from "@/components/qa/SrcsetVerifier";
import heroImg from "../assets/dr-sully-hero-blend.jpg?format=jpg&quality=76&w=1024";
import heroAvifSrcSet from "../assets/dr-sully-hero-blend.jpg?format=avif&quality=60&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import heroWebpSrcSet from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=70&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import heroJpgSrcSet from "../assets/dr-sully-hero-blend.jpg?format=jpg&quality=76&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import { IMAGE_ALT } from "@/content/image-alt";

/**
 * Bare-frame hero portrait used inside <iframe> previews on /qa/hero.
 * Mirrors the exact HeroPortrait props used on the homepage so the
 * object-position clamp and aspect-ratio decisions are validated at each
 * viewport width the parent iframe imposes.
 *
 * Accepts `?x=<css>&y=<css>` query params to override the site-wide
 * `--hero-anchor-x` / `--hero-anchor-y` CSS variables so the parent QA
 * page can live-tune the crop anchor without a rebuild.
 */
export default function QAHeroFrame() {
  // `?perf=1` opts this frame into hero LCP instrumentation so /qa/hero/perf
  // can collect start/load/decoded/lcp timings per viewport.
  const perf =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("perf") === "1";
  // Timing annotations render whenever perf instrumentation is on; `?timings=0`
  // suppresses them for clean screenshot exports.
  const showTimings =
    perf &&
    new URLSearchParams(window.location.search).get("timings") !== "0";
  // `?decodeTarget=<ms>` overrides the breakpoint decode budget for one-off
  // experiments (e.g. holding mobile to a stricter 80ms bar).
  const decodeTargetRaw =
    typeof window !== "undefined"
      ? Number(
          new URLSearchParams(window.location.search).get("decodeTarget"),
        )
      : NaN;
  const decodeTarget =
    Number.isFinite(decodeTargetRaw) && decodeTargetRaw > 0
      ? decodeTargetRaw
      : undefined;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const x = params.get("x");
    const y = params.get("y");
    if (x) document.documentElement.style.setProperty("--hero-anchor-x", x);
    if (y) document.documentElement.style.setProperty("--hero-anchor-y", y);
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-[600px]">
        {/* Center guideline to eyeball whether the face stays inside the
            "strongest band" (roughly the upper-middle third of the frame). */}
        <div className="relative">
          <HeroPortrait
            src={heroImg}
            avifSrcSet={heroAvifSrcSet}
            webpSrcSet={heroWebpSrcSet}
            jpgSrcSet={heroJpgSrcSet}
            sizes="(min-width: 1280px) 480px, (min-width: 1024px) 40vw, (min-width: 640px) 70vw, 100vw"
            alt={IMAGE_ALT.qaHeroFrame}
            priority={perf}
          />
          {/* Rule-of-thirds overlay. Face should sit near the top horizontal line. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10"
          >
            <div className="absolute left-0 right-0 top-1/3 border-t border-dashed border-gold/50" />
            <div className="absolute left-0 right-0 top-2/3 border-t border-dashed border-gold/30" />
            <div className="absolute top-0 bottom-0 left-1/3 border-l border-dashed border-gold/30" />
            <div className="absolute top-0 bottom-0 left-2/3 border-l border-dashed border-gold/30" />
          </div>
          {showTimings && <HeroTimingOverlay />}
        </div>
        {showTimings && (
          <SrcsetVerifier
            srcSets={[heroAvifSrcSet, heroWebpSrcSet, heroJpgSrcSet]}
            decodeTargetMs={decodeTarget}
          />
        )}
      </div>
    </div>
  );
}
